// App.tsx

import "matchmedia-polyfill";
import "matchmedia-polyfill/matchMedia.addListener"; // Add listener for legacy support
import * as React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./styles/global.css";
import Header from "./components/common/components/Header";
import Footer from "./components/common/components/Footer";
import AppTheme from "./theme/AppTheme";
import { Route, Routes } from "react-router-dom";
import { Suspense, lazy, useState } from "react";
import Loader from "./common/Loader"; // Your loader component
import UpdatedSolutionsDetail from "./pages/Solutions/UpdatedDetails";
import FloatingContactButton from "./components/Home/FloatingContactButton";

import PersistUTM from "./PersistUTM";

// Lazy loading components
const Home = lazy(() => import("./pages/Home")); // Lazy loading the Home component
const Challenges = lazy(() => import("./pages/Challenges"));
const Solutions = lazy(() => import("./pages/Solutions"));
const SolutionsDetail = lazy(() => import("./pages/Solutions/details"));
const SolutionsDetails = lazy(() => import("./pages/Solutions/UpdatedDetails"));

const CaseStudy = lazy(() => import("./pages/CaseStudy"));
const CaseStudyDetails = lazy(() => import("./pages/CaseStudy/details"));
const Projects = lazy(() => import("./pages/Projects"));
const About = lazy(() => import("./pages/About"));
const Blogs = lazy(() => import("./pages/Blogs"));
const BlogDetails = lazy(() => import("./pages/BlogDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const TermsAndConditions = lazy(
  () => import("./pages/Policies/TermsAndConditions")
);
const PrivacyPolicies = lazy(() => import("./pages/Policies/PrivacyPolicies"));
const CookiePolicy = lazy(() => import("./pages/Policies/CookiePolicy"));
const NewsPaperGrid = lazy(() => import("./components/About/NewsPaperGrid"));
export default function App(props: Readonly<{ disableCustomTheme?: boolean }>) {
  if (!window.matchMedia) {
    window.matchMedia = function (query: string) {
      let listeners: ((event: MediaQueryListEvent) => void)[] = []; // Store listeners in an array

      const mediaQueryList: MediaQueryList = {
        // Create the MediaQueryList object
        matches: false,
        media: query,
        onchange: null,
        addListener: function (listener: (event: MediaQueryListEvent) => void) {
          listeners.push(listener);
        },
        removeListener: function (
          listener: (event: MediaQueryListEvent) => void
        ) {
          listeners = listeners.filter((l) => l !== listener);
        },
        addEventListener: function (type: string, listener: EventListener) {
          if (type === "change") {
            listeners.push(listener as any);
          }
        },
        removeEventListener: function (type: string, listener: EventListener) {
          if (type === "change") {
            listeners = listeners.filter((l) => l !== (listener as any));
          }
        },
        dispatchEvent: function (event: MediaQueryListEvent) {
          listeners.forEach((listener) => listener(event));
          return true;
        },
      };

      return mediaQueryList; // Return the object
    };
  }

  const [loading, setLoading] = useState<boolean>(false); // Global loading state

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <Header />
      <Container
        component="main"
        maxWidth={false}
        sx={{ minHeight: 300 }}
        data-testid="app-container"
      >
        {/* Global Loader */}
        {loading && <Loader />}

        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            {/* Pass setLoading to the lazy-loaded Home component */}
            <Route path="/" element={<Home setLoading={setLoading} />} />
            <Route
              path="/solutions"
              element={<Solutions setLoading={setLoading} />}
            />
            <Route
              path="/solutions/product-detail/:type/:id"
              element={<SolutionsDetail setLoading={setLoading} />}
            />
            <Route
              path="/solutions/detail/:type/:id"
              element={<UpdatedSolutionsDetail setLoading={setLoading} />}
            />
            <Route
              path="/projects"
              element={<Projects setLoading={setLoading} />}
            />
            <Route
              path="/casestudy"
              element={<CaseStudy setLoading={setLoading} />}
            />
            <Route
              path="/casestudy/details/:id"
              element={<CaseStudyDetails setLoading={setLoading} />}
            />
            <Route path="/about" element={<About setLoading={setLoading} />} />
            <Route path="/blogs" element={<Blogs setLoading={setLoading} />} />
            <Route
              path="/blogs/details/:type/:id"
              element={<BlogDetails setLoading={setLoading} />}
            />
            <Route
              path="/contact"
              element={<Contact setLoading={setLoading} />}
            />
            <Route
              path="/policy/terms&conditions"
              element={<TermsAndConditions setLoading={setLoading} />}
            />
            <Route
              path="/policy/privacy"
              element={<PrivacyPolicies setLoading={setLoading} />}
            />
            <Route
              path="/policy/cookie"
              element={<CookiePolicy setLoading={setLoading} />}
            />
            <Route
              path="/challenges"
              element={<Challenges setLoading={setLoading} />}
            />
            <Route
              path="/news-report"
              element={<NewsPaperGrid setLoading={setLoading} />}
            />
          </Routes>
        </Suspense>
     
       
        <FloatingContactButton />
      </Container>
      <Footer />
    </AppTheme>
  );
}
